import axios, { AxiosInstance } from 'axios'

import { config } from '../configs/api.config'

import { configureClient } from './api-utils'

let client: AxiosInstance

export interface HubspotCompany {
  company_id: string
  company_name: string | null
  domain: string | null
}

export interface HubspotCompaniesResponse {
  results: HubspotCompany[]
  total: number
}

export interface HubspotOrganizationData {
  company_id: string
  company_name: string | null
  domain: string | null
  current_mrr: string | null
  potential_mrr: string | null
  company_size: string | null
  pricing_tier: string | null
  lifecyclestage: string | null
  industry: string | null
  numberofemployees: string | null
  customer_number: string | null
  no_of_users: string | null
  activation_rate: string | null
  active_features: string | null
  usecases_implemented: string | null
  buyer_journey: string | null
  country: string | null
  city: string | null
  properties: Record<string, unknown> | null
}

export interface HubspotCoverageItem {
  org_id: string
  name: string
  organization_use: 'Production' | 'Sandbox'
  parent_production_org_id?: string
  hubspot_company_id?: string
  inherited_parent_hubspot_company_id?: string
  exact_hubspot_match?: HubspotCompany & {
    organization_id: string | null
  }
}

export interface HubspotCoverageResponse {
  summary: {
    total: number
    linked: number
    unlinked: number
    production_total: number
    production_linked: number
    production_unlinked: number
    sandbox_total: number
    sandbox_linked: number
    sandbox_unlinked: number
    exact_unlinked_matches: number
    sandbox_can_inherit_from_parent: number
  }
  exact_unlinked_matches: HubspotCoverageItem[]
  sandbox_can_inherit_from_parent: HubspotCoverageItem[]
  unlinked: HubspotCoverageItem[]
}

export const getOrganizationHubspotClient = () => {
  if (!client) {
    client = axios.create({
      baseURL: config.ORGANIZATION
    })

    configureClient(client, {
      baseURL: config.ORGANIZATION
    })
  }

  return client
}

export const organizationHubspotClient = {
  getHubspotCompanies: async (
    search?: string,
    limit: number = 100
  ): Promise<HubspotCompaniesResponse> => {
    const client = getOrganizationHubspotClient()
    const params: Record<string, string | number> = { limit }

    if (search) {
      params.search = search
    }

    const response = await client.get<HubspotCompaniesResponse>(
      '/v2/hubspot/companies',
      { params }
    )

    return response.data
  },

  getOrganizationHubspotData: async (
    orgId: string
  ): Promise<HubspotOrganizationData | null> => {
    const client = getOrganizationHubspotClient()

    try {
      const response = await client.get<HubspotOrganizationData>(
        `/v2/organizations/${orgId}/hubspot-data`
      )

      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null
      }

      throw error
    }
  },

  getHubspotCompanyById: async (
    companyId: string
  ): Promise<HubspotCompany | null> => {
    const client = getOrganizationHubspotClient()

    try {
      const response = await client.get<HubspotCompany>(
        `/v2/hubspot/companies/${companyId}`
      )

      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null
      }

      throw error
    }
  },

  getOrganizationHubspotCoverage:
    async (): Promise<HubspotCoverageResponse> => {
      const client = getOrganizationHubspotClient()
      const response = await client.get<HubspotCoverageResponse>(
        '/v2/internal/organizations/hubspot-coverage'
      )

      return response.data
    }
}
